import React, { useEffect, useState } from "react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import { BRAND } from "@/lib/brand";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, MessageCircle, Mail, MapPin, Clock, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  useEffect(() => { document.title = `Contact — ${BRAND.name}`; }, []);

  const submit = (e) => {
    e.preventDefault();
    // No backend wired for this iteration — send via WhatsApp deep link
    const text = `Hello Accurate Claims,%0A%0AName: ${encodeURIComponent(form.name)}%0APhone: ${encodeURIComponent(form.phone)}%0AEmail: ${encodeURIComponent(form.email)}%0A%0A${encodeURIComponent(form.message)}`;
    const url = `https://wa.me/916354379879?text=${text}`;
    window.open(url, "_blank");
    setSent(true);
    toast.success("Opening WhatsApp to deliver your enquiry");
  };

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SiteHeader />

      <section className="max-w-[1280px] mx-auto px-5 md:px-8 py-16 md:py-20">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="eyebrow">Contact</div>
            <h1 className="font-heading font-bold text-4xl md:text-5xl mt-4 leading-[1.05]">
              Tell us about your claim.
            </h1>
            <p className="mt-5 text-slate-600 leading-relaxed">
              A senior advisor will get back within 24 working hours. First consultation is on us.
            </p>

            <div className="mt-8 space-y-5">
              <ContactRow icon={<MapPin />} label="Office">
                {BRAND.address.line1},<br />
                {BRAND.address.line2},<br />
                {BRAND.address.city} — {BRAND.address.pincode}, {BRAND.address.state}
              </ContactRow>
              <ContactRow icon={<Phone />} label="Phone">
                <a href={BRAND.telUrl} data-testid="contact-phone-link" className="hover:text-emerald-brand">{BRAND.phoneIntl}</a>
              </ContactRow>
              <ContactRow icon={<MessageCircle />} label="WhatsApp">
                <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="hover:text-emerald-brand">
                  Chat with us
                </a>
              </ContactRow>
              <ContactRow icon={<Mail />} label="Email">
                <a href={BRAND.mailUrl} data-testid="contact-email-link" className="hover:text-emerald-brand">{BRAND.email}</a>
              </ContactRow>
              <ContactRow icon={<Clock />} label="Hours">
                {BRAND.hours}
              </ContactRow>
            </div>
          </div>

          <div className="md:col-span-7">
            <form onSubmit={submit} className="brand-card p-8" data-testid="contact-form">
              {sent ? (
                <div className="py-10 text-center">
                  <div className="w-14 h-14 mx-auto rounded-full bg-emerald-brand/10 text-emerald-brand flex items-center justify-center">
                    <CheckCircle2 className="w-7 h-7" />
                  </div>
                  <h3 className="font-heading font-bold text-2xl mt-5">Message ready on WhatsApp</h3>
                  <p className="text-slate-600 mt-2 text-sm">A new tab should have opened. If not, tap WhatsApp below.</p>
                  <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" className="btn-emerald text-sm mt-6 inline-flex">
                    <MessageCircle className="w-4 h-4" /> Open WhatsApp
                  </a>
                </div>
              ) : (
                <>
                  <h3 className="font-heading font-bold text-2xl">Send a message</h3>
                  <p className="text-sm text-slate-500 mt-1">Your enquiry will be delivered to our team on WhatsApp.</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <Field label="Full name">
                      <Input required value={form.name} onChange={setField("name")} data-testid="contact-name-input" className="rounded-xl" />
                    </Field>
                    <Field label="Phone">
                      <Input required value={form.phone} onChange={setField("phone")} data-testid="contact-phone-input" className="rounded-xl" />
                    </Field>
                    <div className="md:col-span-2">
                      <Field label="Email">
                        <Input type="email" value={form.email} onChange={setField("email")} data-testid="contact-email-input" className="rounded-xl" />
                      </Field>
                    </div>
                    <div className="md:col-span-2">
                      <Field label="How can we help?">
                        <Textarea
                          required
                          rows={5}
                          value={form.message}
                          onChange={setField("message")}
                          placeholder="Tell us about your policy, insurer and what happened with the claim…"
                          data-testid="contact-message-input"
                          className="rounded-xl"
                        />
                      </Field>
                    </div>
                  </div>

                  <button type="submit" data-testid="contact-submit-btn" className="btn-primary text-sm mt-6 w-full md:w-auto">
                    Send via WhatsApp <ArrowRight className="w-4 h-4" />
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 pb-20">
        <div className="rounded-[24px] overflow-hidden brand-shadow border border-slate-200">
          <iframe
            title="Accurate Claims Services on the map"
            data-testid="contact-map"
            src="https://www.google.com/maps?q=Ratna+Business+Square+Ashram+Road+Ahmedabad&output=embed"
            className="w-full h-[420px] border-0"
            loading="lazy"
          />
        </div>
      </section>

      <SiteFooter />
      <WhatsAppFAB />
    </div>
  );
}

function ContactRow({ icon, label, children }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-xl bg-navy/5 text-navy flex items-center justify-center shrink-0">
        {React.cloneElement(icon, { className: "w-4 h-4" })}
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="text-slate-700 mt-1 text-[15px] leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
